"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Small "i" info icon with a plain-English explanation. Opens on hover, click/tap
 * (for touch), and keyboard focus; closes on outside-click or Escape. Dependency-free
 * so it works regardless of the lucide-react version pinned in the project.
 */
export function InfoHint({
  text,
  label,
  className = "",
}: {
  text: string;
  label: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      ref={ref}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={`What is ${label}?`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground/40 text-[10px] font-semibold italic leading-none text-muted-foreground transition-colors hover:border-foreground/60 hover:text-foreground"
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-[calc(100%+6px)] z-50 w-64 max-w-[80vw] -translate-x-1/2 rounded-lg border border-surface-light/40 bg-surface px-3 py-2 text-left text-xs font-normal not-italic leading-relaxed text-muted-foreground shadow-xl"
        >
          {text}
        </span>
      )}
    </span>
  );
}
